import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import Table from "react-bootstrap/Table";
import Icon from "../../../common/Icon";
import Alert from "react-bootstrap/Alert";
import Loader from "../../loader/Loader";

import SomlService from "../../../services/soml-service";
import {FIELD_TO_TITLE_MAP} from "../../../utils/soml-utils";
import {Schema, SchemaFields, SchemaProps} from "../../../types/Schema";
import SchemaRegistryActions from "./SchemaRegistryActions";
import {LocaleDate} from "../../../common/Date";
import CreateSchemaAction from "../actions/CreateSchemaAction";
import GenerateSchemaAction from "../actions/GenerateSchemaAction";
import UploadSchemaAction from "../actions/UploadSchemaAction";
import {WorkbenchStateContext} from "../../context/WorkbenchContext";

import './SchemaRegistry.scss';
import LoaderOverlay from "../../loader/LoaderOverlay";

type SchemaRegistryProps = {
  fields: Array<SchemaFields>
  showActions: boolean
};

type SchemaRegistryState = {
  schemas: Array<Schema>
  hasLoaded: boolean
};

/**
 * Component that loads and renders the available SOML schemas as simple bootstrap table.
 *
 * It allows to specify which columns to show using the fields property except the actions column
 * which is controlled with props.showActions.
 */
class SchemaRegistry extends React.Component<SchemaRegistryProps, SchemaRegistryState> {

  static contextType = WorkbenchStateContext;

  constructor(props: SchemaRegistryProps) {
    super(props);
    this.state = {
      schemas: [],
      hasLoaded: false
    };
    this.onUpdate = this.onUpdate.bind(this);
  }

  componentDidMount() {
    this.loadSchemas();
  }

  loadSchemas() {
    SomlService.getAllSchemas().then((schemas: Array<Schema>) => {
      this.setState({
        schemas,
      });
    }).finally(() => {
      this.setState({
        hasLoaded: true
      });
    });
  }

  onUpdate() {
    this.setState({
      hasLoaded: false,
      schemas: []
    });
    this.loadSchemas();
  }

  render() {
    const loading = !this.state.hasLoaded;
    const noSchemas = this.state.schemas.length < 1 && this.state.hasLoaded;
    const renderTable = !loading && !noSchemas;
    const forbidden = !this.context.canCreate();
    return (
      <div className="SchemaRegistry">
        {
          loading &&
          <Loader size='lg' block={true}/>
        }
        {
          noSchemas &&
          <Alert variant="info" className="NoSchemasAlert">No schemas</Alert>
        }
        {
          renderTable &&
          <SchemaRegistryTable {...this.props} {...this.state} onUpdate={this.onUpdate}/>
        }
        <CreateSchemaAction forbidden={forbidden}/>
        <GenerateSchemaAction forbidden={forbidden}/>
        <UploadSchemaAction standardButton={true} forbidden={forbidden}/>
      </div>
    );
  }
}

type SchemaRegistryTableProps = SchemaRegistryProps & SchemaRegistryState & {
  onUpdate: () => void
};

/**
 * Renders a simple bootstrap table for the given schemas and their fields.
 */
function SchemaRegistryTable(props: SchemaRegistryTableProps) {
  const [executingAction, setExecutingAction] = useState(false);
  const onAction = (executingAction: boolean) => setExecutingAction(executingAction);
  return (
    <div className="SchemaRegistryTableWrapper">
      <Table striped bordered hover size="sm" className="SchemaRegistryTable">
        <thead>
        <SchemaTableHeaders fields={props.fields} showActions={props.showActions}/>
        </thead>
        <tbody>
        <SchemaTableRows fields={props.fields}
                         schemas={props.schemas}
                         onUpdate={props.onUpdate}
                         onAction={onAction}
                         showActions={props.showActions}/>
        </tbody>
      </Table>
      {executingAction ? <LoaderOverlay/> : null}
    </div>
  );
}

/**
 * Lists all table headers that were given to the registry as the fields property.
 */
function SchemaTableHeaders(props: SchemaRegistryProps) {
  const headers = props.fields.map(field => {
    return <th key={field}>{FIELD_TO_TITLE_MAP[field]}</th>;
  });
  if (props.showActions) {
    headers.push(<th key="actions">Actions</th>);
  }
  return (<tr>{headers}</tr>);
}

type SchemaTableRowsProps = SchemaRegistryProps & {
  schemas: Array<Schema>,
  onUpdate: () => void
  onAction: (executingAction: boolean) => void
}

/**
 * Renders the given schemas as table rows. Columns are determined by the provided fields
 * property.
 */
function SchemaTableRows(props: SchemaTableRowsProps) {
  const rows = props.schemas.map(schema => {
    const cols: Array<React.ReactElement> = props.fields.map((field) => {
      if (field === 'id') {
        return (
          <td key={field}>
            <SchemaIdCell schema={schema.properties}/>
          </td>
        );
      }
      if (field === 'bound') {
        return (
          <td key={field}>
            <ActiveSchemaCell field={schema.properties[field]}/>
          </td>
        );
      }
      if (schema.properties[field] instanceof Date) {
        return (
          <td key={field}>
            <LocaleDate date={schema.properties[field]}/>
          </td>
        );
      }
      // Fallback to string field
      return (
        <td key={field}>
          <StringFieldCell field={schema.properties[field]}/>
        </td>
      );
    });

    if (props.showActions) {
      cols.push(<SchemaRegistryActions onUpdate={props.onUpdate} key="actions"
                                       onAction={props.onAction}
                                       schema={schema}/>);
    }
    return (<tr key={schema.properties.id}>{cols}</tr>);
  });
  return <>{rows}</>;
}

function SchemaIdCell(props: { schema: SchemaProps }) {
  return (
    <Link to={`/schema/manage?id=${props.schema.id}`}
          className={"SchemaIdLink text-break"}>
      {props.schema.id}
    </Link>
  );
}

function ActiveSchemaCell(props: any) {
  if (props.field) {
    return <Icon icon="toggle-on" title="Active schema" className="ActiveSchemaToggle"/>
  }
  return <Icon icon="toggle-off" title="Inactive schema" className="InactiveSchemaToggle"/>
}

function StringFieldCell(props: any) {
  if (props.field) {
    return props.field.toString();
  }
  return false;
}

export default SchemaRegistry;
